import React, { useState, useEffect, useCallback } from 'react';

interface IntelFeedPanelProps {
  selectedAOI?: any;
  onEntitySelect: (entity: any) => void;
  onCollapse: () => void;
}

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH: 'bg-orange-500 text-white',
  MEDIUM: 'bg-yellow-500 text-black',
  LOW: 'bg-green-600 text-white',
};


const TYPE_ICONS: Record<string, string> = {
  VESSEL: '\u2693',
  AIRCRAFT: '\u2708',
  PERSON: '\u263A',
  ORGANIZATION: '\u2302',
  LOCATION: '\u2316',
  EVENT: '\u26A0',
  ASSET: '\u2B23',
};

const MODULE_COLORS: Record<string, string> = {
  GEOINT: 'bg-emerald-900/40 text-emerald-300 border-emerald-800/50',
  MARITIME: 'bg-blue-900/40 text-blue-300 border-blue-800/50',
  AIR: 'bg-cyan-900/40 text-cyan-300 border-cyan-800/50',
  TELESCOPE: 'bg-purple-900/40 text-purple-300 border-purple-800/50',
  X_INVESTIGATION: 'bg-slate-900/40 text-slate-300 border-slate-800/50',
  BREACHINT: 'bg-red-900/40 text-red-300 border-red-800/50',
  MONITOR: 'bg-amber-900/40 text-amber-300 border-amber-800/50',
};

const ENTITY_TYPE_OPTIONS = ['AIRCRAFT', 'VESSEL', 'LOCATION', 'PERSON', 'ORGANIZATION', 'EVENT', 'ASSET'] as const;

const IntelFeedPanel: React.FC<IntelFeedPanelProps> = ({ selectedAOI, onEntitySelect, onCollapse }) => {
  const [briefing, setBriefing] = useState<any>(null);
  const [correlationResults, setCorrelationResults] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [polAssessment, setPolAssessment] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string>('briefing');
  // Entity type filter — EVENT hidden by default (change-detection noise from other AOIs)
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set(['EVENT']));

  const getAOICenter = useCallback(() => {
    if (!selectedAOI?.geometry) return null;
    const geom = selectedAOI.geometry;
    if (geom.type === 'Point' && geom.coordinates) {
      return { lat: geom.coordinates[1], lon: geom.coordinates[0] };
    }
    const coords = geom.coordinates?.[0] || [];
    if (!coords.length) return null;
    const lons = coords.map((c: number[]) => c[0]);
    const lats = coords.map((c: number[]) => c[1]);
    return {
      lat: lats.reduce((a: number, b: number) => a + b, 0) / lats.length,
      lon: lons.reduce((a: number, b: number) => a + b, 0) / lons.length,
    };
  }, [selectedAOI]);

  // Clear stale data immediately when AOI changes
  useEffect(() => {
    setBriefing(null);
    setCorrelationResults([]);
    setPolAssessment(null);
    setNotifications([]);
  }, [selectedAOI?.id, selectedAOI?.aoi_id]);

  // Fetch all intel when AOI changes
  useEffect(() => {
    const center = getAOICenter();
    if (!center) return;

    const aoiId = selectedAOI?.id || selectedAOI?.aoi_id;
    const aoiIdParam = aoiId && aoiId !== 'unknown' ? aoiId : null;

    setLoading(true);

    const token = getAuthToken();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    // Fetch briefing, correlation, and PoL in parallel
    Promise.all([
      // Briefing — only when we have a real saved AOI id
      aoiIdParam
        ? fetch(`/api/entities/briefing/${aoiIdParam}?lat=${center.lat}&lon=${center.lon}&radius_km=10&hours=72`, { headers })
            .then(r => r.ok ? r.json() : null).catch(() => null)
        : Promise.resolve(null),
      // Correlation — exclude EVENT to avoid change-detection noise from unrelated AOIs
      fetch(`/api/entities/correlate/location?lat=${center.lat}&lon=${center.lon}&radius_km=15&hours=48`, { headers })
        .then(r => r.ok ? r.json() : null)
        .catch(() => null),
      // PoL assessment
      aoiIdParam
        ? fetch('/api/entities/pol/assess', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ aoi_id: aoiIdParam, lat: center.lat, lon: center.lon, change_percent: 0, days: 90 }),
          }).then(r => r.ok ? r.json() : null).catch(() => null)
        : Promise.resolve(null),
      // Notifications — scoped to AOI when id available
      aoiIdParam
        ? fetch(`/api/geoint/wayback/notifications?limit=10&unread_only=true&aoi_id=${aoiIdParam}`, { headers })
            .then(r => r.ok ? r.json() : null).catch(() => null)
        : fetch(`/api/geoint/wayback/notifications?limit=10&unread_only=true`, { headers })
            .then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([briefData, corrData, polData, notifData]) => {
      setBriefing(briefData);
      setCorrelationResults(corrData?.results || []);
      setPolAssessment(polData);
      setNotifications(notifData?.notifications || []);
      setLoading(false);
    });
  }, [selectedAOI?.id, selectedAOI?.aoi_id, getAOICenter]);

  // Refresh notifications periodically (scoped to current AOI)
  useEffect(() => {
    const aoiId = selectedAOI?.id || selectedAOI?.aoi_id;
    const aoiIdParam = aoiId && aoiId !== 'unknown' ? aoiId : null;
    const fetchNotifs = async () => {
      try {
        const token = getAuthToken();
        const url = aoiIdParam
          ? `/api/geoint/wayback/notifications?limit=10&unread_only=true&aoi_id=${aoiIdParam}`
          : `/api/geoint/wayback/notifications?limit=10&unread_only=true`;
        const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (resp.ok) {
          const data = await resp.json();
          setNotifications(data.notifications || []);
        }
      } catch { /* ignore */ }
    };
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [selectedAOI?.id, selectedAOI?.aoi_id]);

  const toggleSection = (key: string) => {
    setExpandedSection(expandedSection === key ? '' : key);
  };

  const addToWatchlist = async (entityId: string) => {
    try {
      const token = getAuthToken();
      await fetch(`/api/entities/${entityId}/watchlist`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch { /* ignore */ }
  };

  const severity = briefing?.severity || 'LOW';

  return (
    <div className="h-full flex flex-col bg-card border-l border-border">
      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Intel Feed</h3>
          {briefing && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${SEVERITY_COLORS[severity] || 'bg-gray-600 text-white'}`}>
              {severity}
            </span>
          )}
        </div>
        <button
          onClick={onCollapse}
          className="text-muted-foreground hover:text-foreground text-sm px-1"
          title="Collapse panel"
        >
          &raquo;
        </button>
      </div>

      {!selectedAOI?.geometry ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-xs text-muted-foreground text-center">
            Select an AOI to view intelligence feed
          </p>
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-cyan-400 border-t-transparent rounded-full" />
            <span className="text-xs text-muted-foreground">Loading intel...</span>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Alerts section */}
          {notifications.length > 0 && (
            <div className="border-b border-border">
              <button
                onClick={() => toggleSection('alerts')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/30 transition-colors"
              >
                <span className="text-xs font-medium text-foreground">
                  Alerts ({notifications.length})
                </span>
                <span className="text-xs text-muted-foreground">
                  {expandedSection === 'alerts' ? '-' : '+'}
                </span>
              </button>
              {expandedSection === 'alerts' && (
                <div className="px-3 pb-2 space-y-1.5">
                  {notifications.slice(0, 5).map((notif: any, i: number) => (
                    <div
                      key={i}
                      className={`text-xs p-2 rounded border ${
                        notif.severity === 'critical' ? 'border-red-600 bg-red-950/20' :
                        notif.severity === 'high' ? 'border-orange-600 bg-orange-950/20' :
                        'border-border bg-muted/30'
                      }`}
                    >
                      <div className="font-medium text-foreground">{notif.title}</div>
                      <div className="text-muted-foreground mt-0.5">{notif.message?.slice(0, 120)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Briefing section */}
          {briefing && (
            <div className="border-b border-border">
              <button
                onClick={() => toggleSection('briefing')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/30 transition-colors"
              >
                <span className="text-xs font-medium text-foreground">
                  AOI Briefing
                </span>
                <span className="text-xs text-muted-foreground">
                  {expandedSection === 'briefing' ? '-' : '+'}
                </span>
              </button>
              {expandedSection === 'briefing' && (
                <div className="px-3 pb-2 space-y-2">
                  {/* Summary */}
                  {briefing.summary && (
                    <p className="text-xs text-muted-foreground">{briefing.summary}</p>
                  )}

                  {/* Severity reasons */}
                  {briefing.severity_reasons?.length > 0 && (
                    <div className="space-y-1">
                      {briefing.severity_reasons.map((reason: string, i: number) => (
                        <div key={i} className="text-xs flex items-start gap-1.5">
                          <span className="text-orange-400 mt-0.5 shrink-0">!</span>
                          <span className="text-foreground">{reason}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Active modules */}
                  {briefing.modules_active?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {briefing.modules_active.map((mod: string) => (
                        <span
                          key={mod}
                          className={`text-[10px] px-1.5 py-0.5 rounded border ${MODULE_COLORS[mod] || 'bg-muted text-foreground border-border'}`}
                        >
                          {mod}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Threat indicators */}
                  {briefing.threat_indicators && (
                    <div className="space-y-1">
                      {briefing.threat_indicators.dark_vessel_count > 0 && (
                        <div className="text-xs text-red-300">
                          {briefing.threat_indicators.dark_vessel_count} AIS-dark vessel(s)
                        </div>
                      )}
                      {briefing.threat_indicators.military_aircraft_count > 0 && (
                        <div className="text-xs text-red-300">
                          {briefing.threat_indicators.military_aircraft_count} military aircraft
                        </div>
                      )}
                      {briefing.threat_indicators.has_watchlisted_entity && (
                        <div className="text-xs text-red-300">Watchlisted entity detected</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Correlation section */}
          {correlationResults.length > 0 && (
            <div className="border-b border-border">
              <button
                onClick={() => toggleSection('correlation')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/30 transition-colors"
              >
                <span className="text-xs font-medium text-foreground">
                  Nearby Entities ({correlationResults.filter((h: any) => !hiddenTypes.has((h.entity?.entity_type || '').toUpperCase())).length})
                </span>
                <span className="text-xs text-muted-foreground">
                  {expandedSection === 'correlation' ? '-' : '+'}
                </span>
              </button>
              {expandedSection === 'correlation' && (
                <div className="px-3 pb-2 space-y-1.5">
                  {/* Type filter chips */}
                  <div className="flex flex-wrap gap-1 pb-1.5 border-b border-border/50">
                    {ENTITY_TYPE_OPTIONS.map(type => {
                      const count = correlationResults.filter((h: any) =>
                        (h.entity?.entity_type || '').toUpperCase() === type).length;
                      if (count === 0) return null;
                      const hidden = hiddenTypes.has(type);
                      return (
                        <button key={type} onClick={() => setHiddenTypes(prev => {
                          const next = new Set(prev);
                          hidden ? next.delete(type) : next.add(type);
                          return next;
                        })}
                          className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${
                            hidden
                              ? 'border-border text-muted-foreground/50 line-through'
                              : 'border-primary/40 text-foreground'
                          }`}
                          title={hidden ? `Show ${type}` : `Hide ${type}`}
                        >
                          {TYPE_ICONS[type]} {type} {count}
                        </button>
                      );
                    })}
                  </div>
                  {correlationResults
                    .filter((hit: any) => !hiddenTypes.has((hit.entity?.entity_type || '').toUpperCase()))
                    .map((hit: any, i: number) => {
                    const entity = hit.entity || {};
                    return (
                      <div
                        key={i}
                        className="text-xs p-2 rounded border border-border hover:border-primary/50 transition-colors cursor-pointer"
                        onClick={() => onEntitySelect(entity)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span>{TYPE_ICONS[entity.entity_type] || '?'}</span>
                            <span className="text-foreground font-medium truncate max-w-[140px]">{entity.name}</span>
                          </div>
                          <span className="text-muted-foreground shrink-0">{hit.distance_km?.toFixed(1)}km</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                          <span>{entity.entity_type}</span>
                          <span>{hit.observation_count} obs</span>
                          {hit.modules_present?.length > 0 && (
                            <span>{hit.modules_present.length} modules</span>
                          )}
                        </div>
                        {(entity.entity_type === 'VESSEL' || entity.entity_type === 'AIRCRAFT') && (
                          entity.attributes?.detection_source ? (
                            <span
                              className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30"
                              title="Inferred from satellite imagery object detection (YOLO) — not a confirmed transponder track"
                            >
                              ML detection · {entity.attributes?.confidence != null ? Math.round(entity.attributes.confidence * 100) : '?'}%
                            </span>
                          ) : (
                            <span
                              className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                              title="Confirmed via live transponder tracking (AIS / ADS-B / FR24 / VesselAPI)"
                            >
                              {entity.entity_type === 'VESSEL' ? 'AIS-confirmed' : 'Transponder-confirmed'}
                            </span>
                          )
                        )}
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); addToWatchlist(entity.entity_id); }}
                            className="text-[10px] text-amber-400 hover:underline"
                          >
                            + Watchlist
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Pattern of Life section */}
          {polAssessment && (
            <div className="border-b border-border">
              <button
                onClick={() => toggleSection('pol')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/30 transition-colors"
              >
                <span className="text-xs font-medium text-foreground">Pattern of Life</span>
                <span className="text-xs text-muted-foreground">
                  {expandedSection === 'pol' ? '-' : '+'}
                </span>
              </button>
              {expandedSection === 'pol' && (
                <div className="px-3 pb-2 space-y-2">
                  {polAssessment.deviation ? (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Deviation Score</span>
                        <span className="text-foreground font-medium">
                          {polAssessment.deviation.score?.toFixed(3) ?? 'N/A'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {polAssessment.deviation.assessment}
                      </div>
                      {/* Score bar */}
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            (polAssessment.deviation.score || 0) > 0.7 ? 'bg-red-500' :
                            (polAssessment.deviation.score || 0) > 0.5 ? 'bg-orange-500' :
                            (polAssessment.deviation.score || 0) > 0.3 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min((polAssessment.deviation.score || 0) * 100, 100)}%` }}
                        />
                      </div>
                      {/* Factor breakdown */}
                      {polAssessment.deviation.factors && (
                        <div className="space-y-1 pt-1">
                          {Object.entries(polAssessment.deviation.factors).map(([key, val]: [string, any]) => (
                            <div key={key} className="flex justify-between text-[10px]">
                              <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                              <span className="text-foreground">{typeof val === 'number' ? val.toFixed(2) : String(val)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {polAssessment.note || 'Insufficient observation history for baseline'}
                    </p>
                  )}

                  {polAssessment.severity && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-[10px] text-muted-foreground">Severity:</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        SEVERITY_COLORS[polAssessment.severity] || 'bg-gray-600 text-white'
                      }`}>
                        {polAssessment.severity}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="px-3 py-2 text-[10px] text-muted-foreground">
            {briefing?.generated_at && (
              <span>Updated: {new Date(briefing.generated_at).toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelFeedPanel;
